import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  Tooltip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Delete, Refresh, Warning, ChevronLeft, ChevronRight, CheckCircle } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { 
  getTranslationSettings, 
  createTranslationSetting, 
  updateTranslationSetting, 
  deleteTranslationSetting, 
  activateTranslationSetting 
} from '../../services/searchAdmin';

const defaultForm = {
  service: 'google',
  google_api_key: '',
  baidu_app_id: '',
  baidu_secret_key: '',
  source_language: 'en',
  target_language: 'zh',
  is_active: false
};

const TranslateSettings = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Form state
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  
  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState({ open: false, settingId: null, settingName: '' });

  // Fetch translation settings
  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getTranslationSettings();
      setSettings(response || []);
    } catch (err) {
      setError(err.message);
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Refresh function
  const handleRefresh = () => {
    fetchSettings();
  };

  // Form handlers
  const handleOpen = (row) => {
    if (row) {
      setEditId(row.id);
      setForm({ 
        service: row.service || 'google',
        google_api_key: row.google_api_key || '',
        baidu_app_id: row.baidu_app_id || '',
        baidu_secret_key: row.baidu_secret_key || '',
        source_language: row.source_language || 'en',
        target_language: row.target_language || 'zh',
        is_active: Boolean(row.is_active)
      });
    } else {
      setEditId(null);
      setForm(defaultForm);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setForm(defaultForm);
    setEditId(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle boolean values properly for select fields
    let finalValue = value;
    if (name === 'is_active') {
      finalValue = value === 'true' || value === true;
    }
    
    setForm({ 
      ...form, 
      [name]: finalValue
    });
  };

  const handleSave = async () => {
    if (!form.service) {
      setSnackbar({ open: true, message: t('translateSettings:messages.serviceRequired'), severity: 'error' });
      return;
    }

    // Validate Baidu fields if service is Baidu
    if (form.service === 'baidu') {
      if (!form.baidu_app_id || !form.baidu_secret_key) {
        setSnackbar({ open: true, message: t('translateSettings:messages.baiduFieldsRequired'), severity: 'error' });
        return;
      }
    }

    try {
      if (editId) {
        await updateTranslationSetting(editId, form);
        setSnackbar({ open: true, message: t('translateSettings:messages.updatedSuccess'), severity: 'success' });
      } else {
        await createTranslationSetting(form);
        setSnackbar({ open: true, message: t('translateSettings:messages.addedSuccess'), severity: 'success' });
      }
      handleClose();
      fetchSettings(); // Refresh the list
    } catch (error) {
      console.error('Error saving translation setting:', error);
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteTranslationSetting(id);
      setSnackbar({ open: true, message: t('translateSettings:messages.deletedSuccess'), severity: 'info' });
      setDeleteModal({ open: false, settingId: null, settingName: '' });
      fetchSettings(); // Refresh the list
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  const handleDeleteClick = (setting) => {
    const displayName = `${setting.get_service_display || setting.service} (${setting.source_language} → ${setting.target_language})`;
    setDeleteModal({
      open: true,
      settingId: setting.id,
      settingName: displayName
    });
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ open: false, settingId: null, settingName: '' });
  };

  const handleDeleteConfirm = () => {
    if (deleteModal.settingId) {
      handleDelete(deleteModal.settingId);
    }
  };

  const handleActivate = async (id) => {
    try {
      await activateTranslationSetting(id);
      setSnackbar({ open: true, message: t('translateSettings:messages.activatedSuccess'), severity: 'success' });
      fetchSettings(); // Refresh the list
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  // Get service color
  const getServiceColor = (service) => {
    switch (service) {
      case 'google': return '#4285F4';
      case 'baidu': return '#2932E1';
      default: return '#757575';
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    return status ? '#4CAF50' : '#F44336';
  };

  // Add serial numbers to settings
  const addSerialNumbers = (data) =>
    data.map((row, idx) => ({
      ...row,
      sn: idx + 1
    }));

  const getColumns = () => [
    { field: 'sn', headerName: t('translateSettings:table.sn'), width: 80, sortable: false, filterable: false },
    
    { 
      field: 'service', 
      headerName: t('translateSettings:table.service'), 
      flex: 1, 
      minWidth: 120,
      renderCell: (params) => {
        const service = params.row?.service || 'unknown';
        return (
          <Chip
            label={service.charAt(0).toUpperCase() + service.slice(1)}
            size="small"
            sx={{
              backgroundColor: getServiceColor(service),
              color: 'white',
              fontWeight: 'bold'
            }}
          />
        );
      }
    },
    
    { 
      field: 'source_language', 
      headerName: t('translateSettings:table.source'), 
      flex: 1, 
      minWidth: 100,
      renderCell: (params) => {
        const source = params.row?.source_language;
        return <span>{source ? source.toUpperCase() : 'N/A'}</span>;
      }
    },
    
    { 
      field: 'target_language', 
      headerName: t('translateSettings:table.target'), 
      flex: 1, 
      minWidth: 100,
      renderCell: (params) => {
        const target = params.row?.target_language;
        return <span>{target ? target.toUpperCase() : 'N/A'}</span>;
      }
    },
    
    { 
      field: 'is_active', 
      headerName: t('translateSettings:table.status'), 
      flex: 1, 
      minWidth: 100,
      renderCell: (params) => {
        const isActive = params.row?.is_active;
        // Handle boolean conversion properly
        let activeStatus = false;
        if (typeof isActive === 'boolean') {
          activeStatus = isActive;
        } else if (typeof isActive === 'string') {
          activeStatus = isActive.toLowerCase() === 'true';
        } else if (typeof isActive === 'number') {
          activeStatus = isActive === 1;
        }
        
        return (
          <Chip
            label={activeStatus ? t('translateSettings:status.active') : t('translateSettings:status.inactive')}
            size="small"
            sx={{
              backgroundColor: getStatusColor(activeStatus),
              color: 'white',
              fontWeight: 'bold'
            }}
          />
        );
      }
    },
    
    { 
      field: 'created_at', 
      headerName: t('translateSettings:table.created'), 
      flex: 1, 
      minWidth: 120,
      renderCell: (params) => {
        const createdAt = params.row?.created_at;
        if (!createdAt) return <span>N/A</span>;
        
        try {
          const date = new Date(createdAt);
          if (isNaN(date.getTime())) {
            return <span>N/A</span>;
          }
          return <span>{date.toLocaleDateString()}</span>;
        } catch (error) {
          console.error('Error formatting date:', error, createdAt);
          return <span>N/A</span>;
        }
      }
    },
    
    { 
      field: 'updated_at', 
      headerName: t('translateSettings:table.updated'), 
      flex: 1, 
      minWidth: 120,
      renderCell: (params) => {
        const updatedAt = params.row?.updated_at;
        if (!updatedAt) return <span>N/A</span>;
        
        try {
          const date = new Date(updatedAt);
          if (isNaN(date.getTime())) {
            return <span>N/A</span>;
          }
          return <span>{date.toLocaleDateString()}</span>;
        } catch (error) {
          console.error('Error formatting date:', error, updatedAt);
          return <span>N/A</span>;
        }
      }
    },
    
    {
      field: 'actions',
      headerName: t('translateSettings:table.actions'),
      width: 200,
      sortable: false,
      renderCell: (params) => {
        if (!params || !params.row) return null;
        const isActive = params.row?.is_active;
        
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!isActive && (
              <Tooltip title={t('translateSettings:tooltips.activate')}>
                <IconButton 
                  color="success" 
                  onClick={() => handleActivate(params.row.id)}
                  size="small"
                >
                  <CheckCircle />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t('translateSettings:tooltips.edit')}>
              <IconButton color="primary" onClick={() => handleOpen(params.row)} size="small">
                <Edit />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('translateSettings:tooltips.delete')}>
              <IconButton color="error" onClick={() => handleDeleteClick(params.row)} size="small">
                <Delete />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }
    }
  ];

  if (loading && settings.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>{t('translateSettings:loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          {t('translateSettings:title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{ minWidth: 'auto' }}
          >
            {t('translateSettings:refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' }, minWidth: 'auto' }}
            onClick={() => handleOpen()}
          >
            {t('translateSettings:addSetting')}
          </Button>
        </Box>
      </Box>
      
      <Box sx={{ height: 520, width: '100%', background: 'white', borderRadius: 2, boxShadow: 1 }}>
        {settings.length === 0 && !loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column' }}>
            <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
              {t('translateSettings:noSettings')}
            </Typography>
            <Button variant="outlined" onClick={() => handleOpen()}>
              {t('translateSettings:addFirstSetting')}
            </Button>
          </Box>
        ) : (
          <DataGrid
            rows={addSerialNumbers(settings)}
            columns={getColumns()}
            pageSize={20}
            rowsPerPageOptions={[]}
            disableSelectionOnClick
            loading={loading}
            hideFooter
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: theme => theme.palette.primary.main,
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1rem'
              },
              '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                color: 'white'
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#FFF0F3'
              },
              '& .MuiDataGrid-cell': {
                fontSize: '1rem'
              }
            }}
          />
        )}
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: '#DC143C', fontWeight: 'bold' }}>
          {editId ? t('translateSettings:dialog.editTitle') : t('translateSettings:dialog.addTitle')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select
              label={t('translateSettings:dialog.service')}
              name="service"
              value={form.service}
              onChange={handleChange}
              required
              fullWidth
            >
              <MenuItem value="google">{t('translateSettings:services.google')}</MenuItem>
              <MenuItem value="baidu">{t('translateSettings:services.baidu')}</MenuItem>
            </TextField>
            
            {form.service === 'google' && (
              <TextField 
                label={t('translateSettings:dialog.googleApiKey')} 
                name="google_api_key" 
                value={form.google_api_key} 
                onChange={handleChange} 
                fullWidth 
                helperText={t('translateSettings:dialog.googleApiKeyHelper')}
              />
            )}
            
            {form.service === 'baidu' && (
              <>
                <TextField 
                  label={t('translateSettings:dialog.baiduAppId')} 
                  name="baidu_app_id" 
                  value={form.baidu_app_id} 
                  onChange={handleChange} 
                  required 
                  fullWidth 
                />
                <TextField 
                  label={t('translateSettings:dialog.baiduSecretKey')} 
                  name="baidu_secret_key" 
                  value={form.baidu_secret_key} 
                  onChange={handleChange} 
                  required 
                  fullWidth 
                  type="password"
                />
              </>
            )}
            
            <TextField
              select
              label={t('translateSettings:dialog.sourceLanguage')}
              name="source_language"
              value={form.source_language}
              onChange={handleChange}
              required
              fullWidth
            >
              <MenuItem value="en">{t('translateSettings:languages.en')}</MenuItem>
              <MenuItem value="zh">{t('translateSettings:languages.zh')}</MenuItem>
              <MenuItem value="es">{t('translateSettings:languages.es')}</MenuItem>
              <MenuItem value="fr">{t('translateSettings:languages.fr')}</MenuItem>
              <MenuItem value="de">{t('translateSettings:languages.de')}</MenuItem>
              <MenuItem value="ja">{t('translateSettings:languages.ja')}</MenuItem>
              <MenuItem value="ko">{t('translateSettings:languages.ko')}</MenuItem>
              <MenuItem value="ru">{t('translateSettings:languages.ru')}</MenuItem>
              <MenuItem value="ar">{t('translateSettings:languages.ar')}</MenuItem>
              <MenuItem value="hi">{t('translateSettings:languages.hi')}</MenuItem>
            </TextField>
            
            <TextField
              select
              label={t('translateSettings:dialog.targetLanguage')}
              name="target_language"
              value={form.target_language}
              onChange={handleChange}
              required
              fullWidth
            >
              <MenuItem value="en">{t('translateSettings:languages.en')}</MenuItem>
              <MenuItem value="zh">{t('translateSettings:languages.zh')}</MenuItem>
              <MenuItem value="es">{t('translateSettings:languages.es')}</MenuItem>
              <MenuItem value="fr">{t('translateSettings:languages.fr')}</MenuItem>
              <MenuItem value="de">{t('translateSettings:languages.de')}</MenuItem>
              <MenuItem value="ja">{t('translateSettings:languages.ja')}</MenuItem>
              <MenuItem value="ko">{t('translateSettings:languages.ko')}</MenuItem>
              <MenuItem value="ru">{t('translateSettings:languages.ru')}</MenuItem>
              <MenuItem value="ar">{t('translateSettings:languages.ar')}</MenuItem>
              <MenuItem value="hi">{t('translateSettings:languages.hi')}</MenuItem>
            </TextField>
            
            <TextField
              select
              label={t('translateSettings:dialog.status')}
              name="is_active"
              value={form.is_active}
              onChange={handleChange}
              required
              fullWidth
            >
              <MenuItem value={true}>{t('translateSettings:status.active')}</MenuItem>
              <MenuItem value={false}>{t('translateSettings:status.inactive')}</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('translateSettings:dialog.cancel')}</Button>
          <Button onClick={handleSave} variant="contained" sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}>
            {editId ? t('translateSettings:dialog.update') : t('translateSettings:dialog.add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteModal.open} 
        onClose={handleDeleteCancel}
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          color: '#DC143C', 
          fontWeight: 'bold',
          pb: 1
        }}>
          <Warning sx={{ color: '#DC143C', fontSize: 28 }} />
          {t('translateSettings:delete.title')}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2, color: '#333' }}>
            {t('translateSettings:delete.message')} <strong>{deleteModal.settingName}</strong>?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ 
            backgroundColor: '#FFF3E0', 
            p: 2, 
            borderRadius: 1, 
            border: '1px solid #FFB74D',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Warning sx={{ color: '#F57C00', fontSize: 20 }} />
            {t('translateSettings:delete.warning')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={handleDeleteCancel}
            variant="outlined"
            sx={{ 
              borderColor: '#666', 
              color: '#666',
              '&:hover': { 
                borderColor: '#333', 
                backgroundColor: '#f5f5f5' 
              }
            }}
          >
            {t('translateSettings:dialog.cancel')}
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            sx={{ 
              backgroundColor: '#DC143C', 
              '&:hover': { backgroundColor: '#B91C3C' },
              px: 3
            }}
            startIcon={<Delete />}
          >
            {t('translateSettings:delete.deleteSetting')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TranslateSettings;